import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Result, Spin, Alert, Typography } from "antd";
import { LoadingOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { getTenantId } from "../../../utils/auth";
import { JobType } from "../../../constants/JobTypes";
import { StepConfigurationData } from "./StepConfiguration";

const { Text } = Typography;

interface StepProcessingProps {
  jobConfigId: string;
  stepConfigurationData: StepConfigurationData;
  onSuccess: () => void;
}

// Interstitial between Step 2 (Configuration) and Step 3 (Power Analysis) - not
// one of the wireframe's 5 rail steps itself. The file was already uploaded to S3
// right after Step 1 (see CreateCausalLoop.tsx's handleUploadNext - Step 2's
// market-suggestion call needs it there already), so this step only creates the
// JobConfig + JobRun (one job, both engines run concurrently server-side - see
// process_causal_loop_job in sqs_listener_service.py), then polls that JobRun
// until it's done. "Go to Dashboard" is always available so the user isn't stuck
// waiting on a multi-minute Geo Lift computation - see causal-loop-feature/plan.md.
const POLL_INTERVAL_MS = 15000;
const MAX_POLL_ATTEMPTS = 80; // ~20 minutes

const StepProcessing: React.FC<StepProcessingProps> = ({
  jobConfigId,
  stepConfigurationData,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"creating" | "polling" | "done" | "failed">("creating");
  const [error, setError] = useState<string | null>(null);
  const submittedRef = useRef(false);
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    submitJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitJob = async () => {
    setPhase("creating");
    setError(null);
    try {
      const cfg = stepConfigurationData;
      const payload = {
        jobConfigId,
        name: cfg.jobName,
        jobType: JobType.CausalLoop,
        tenantId: getTenantId(),
        dataSourceConfig: { dataSourceType: "FILE_CSV" },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            bidMultiplier: {
              columnMapping: {
                age_varialbes: cfg.age ? [cfg.age] : undefined,
                device_platform_varialbes: cfg.devicePlatform ? [cfg.devicePlatform] : undefined,
                gender_varialbes: cfg.gender ? [cfg.gender] : undefined,
                locale_varialbes: cfg.locale ? [cfg.locale] : undefined,
                region_varialbes: [cfg.region],
                user_os_varialbes: cfg.userOs ? [cfg.userOs] : undefined,
                target_variables: [cfg.targetVariable],
              },
            },
            geoLift: {
              columnMapping: {
                date_variables: [cfg.dateColumn],
                kpi_variables: [cfg.targetVariable],
                location_variables: [cfg.region],
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            campaignObjective: cfg.campaignObjective,
            geoLocationType: cfg.geoLocationType,
            dateFormat: "%Y-%m-%d",
            scmType: cfg.scmType,
            isSales: cfg.isSales,
            campaignStartDate: cfg.campaignStartDate,
            buyType: "national",
            includeMarket: cfg.selectedMarkets,
            bauSpent: cfg.bauSpent,
            statSigThreshold: cfg.statSigThreshold,
          },
        },
        modelOutputConfig: {},
      };

      await axiosInstance.post("/api/jobConfig", payload);
      const runResponse = await axiosInstance.post("/api/jobRun", { jobConfigId });
      const jobRunId = (runResponse.data?.jobRun as any)?.jobRunId;

      setPhase("polling");
      pollStatus(jobRunId);
    } catch (err: any) {
      console.error("Error submitting Causal Loop job:", err);
      setError(
        err?.response?.data?.detail ||
          "Something went wrong while creating the job. Please try again."
      );
      setPhase("failed");
    }
  };

  const pollStatus = (jobRunId: string | undefined) => {
    if (!jobRunId) {
      // Job was submitted successfully even if we can't poll it directly - the
      // job list page will still show its live status.
      return;
    }
    const check = async () => {
      pollAttemptsRef.current += 1;
      try {
        const response = await axiosInstance.get(`/api/jobRun/${jobRunId}`);
        const status = response.data?.jobRun?.jobStatus;
        if (status === "SUCCESSFUL") {
          setPhase("done");
          onSuccess();
          return;
        }
        if (status === "FAILED") {
          setError(
            "Both engines failed to complete. Please check your data and try again, or view the job from the list for details."
          );
          setPhase("failed");
          return;
        }
      } catch (e) {
        // A transient fetch error shouldn't stop polling - keep trying until
        // MAX_POLL_ATTEMPTS.
      }
      if (pollAttemptsRef.current < MAX_POLL_ATTEMPTS) {
        setTimeout(check, POLL_INTERVAL_MS);
      }
    };
    setTimeout(check, POLL_INTERVAL_MS);
  };

  const goToDashboard = () => navigate("/causal-loop/list");

  if (phase === "failed") {
    return (
      <Card className="shadow-sm">
        <Result
          status="error"
          title="Job Submission Failed"
          subTitle={error}
          icon={<ExclamationCircleOutlined className="text-red-500 text-6xl" />}
          extra={[
            <Button type="primary" key="retry" onClick={() => { submittedRef.current = false; submitJob(); }}>
              Try Again
            </Button>,
            <Button key="dashboard" onClick={goToDashboard}>
              Go to Dashboard
            </Button>,
          ]}
        />
      </Card>
    );
  }

  const phaseLabel =
    phase === "creating" ? "Creating job..." : "Running Bid Optimizer + Geo Lift analysis...";

  return (
    <Card className="shadow-sm text-center py-12">
      <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
      <h3 className="text-lg font-semibold text-gray-900 mt-6">
        {phaseLabel}
      </h3>
      <Text className="text-sm text-gray-500 block max-w-md mx-auto mb-2">
        Bid Optimizer and Geo Lift are running in parallel against your uploaded data. Geo Lift's
        market selection can take several minutes - feel free to head to the dashboard and come
        back once it's done; this study's status will show as "Running" until then.
      </Text>
      {phase === "polling" && (
        <Alert
          className="mt-4 max-w-md mx-auto text-left"
          type="info"
          showIcon
          message="You can safely leave this page"
          description='This study will keep processing in the background. Come back to "Causal Loop" from the sidebar and click "View" once it shows as completed.'
        />
      )}
      <div className="mt-8">
        <Button type="primary" size="large" onClick={goToDashboard}>
          Go to Dashboard
        </Button>
      </div>
    </Card>
  );
};

export default StepProcessing;
